import { create, type StateCreator } from 'zustand'
import { devtools } from 'zustand/middleware'

import { CompanyDetailsModel } from 'app/models/companyDetailsModel'
import { extractUrlParam, matchUrlSegment, UrlSegment } from 'app/common/routeUtils'
import { zustandPlugin } from '@/modules/shared/openreplay'

const COMPANY_ID = 'companyId'
const MATCH_ROUTE = '/company/:companyId'
const MATCH_URL = '/companySelection'

type State = {
  companies: CompanyDetailsModel[]
  selectedCompany: CompanyDetailsModel
}

type Actions = {
  setCompanies: (companies: CompanyDetailsModel[]) => void
  chooseCompany: (company: CompanyDetailsModel) => void
  /** Wywoływane przy każdej zmianie trasy — patrz `RouterBridge`. */
  syncFromPath: (pathname: string) => void
}

type StoreMutators = [['zustand/devtools', never]]

const trackedCreator = zustandPlugin as unknown as <T>(
  creator: StateCreator<T, StoreMutators, []>,
  name?: string,
) => StateCreator<T, StoreMutators, []>

const companyFromUrl = (): CompanyDetailsModel =>
  ({
    id: extractUrlParam(MATCH_ROUTE, COMPANY_ID),
    companyName: null,
  }) as CompanyDetailsModel

/**
 * Wybrana firma i lista firm do wyboru, wcześniej slice `companySelection`.
 *
 * Identyfikator pochodzi z adresu — to on jest źródłem prawdy, a nie wybór zapisany
 * w pamięci. Wyjątkiem jest ekran `/companySelection`, gdzie trzeba utrzymać firmę
 * właśnie wskazaną przez użytkownika, zanim nastąpi przejście dalej.
 *
 * Zachowanie przeniesione 1:1 z reducera, łącznie z tym, że każda inna nawigacja
 * wyprowadza firmę z URL-a na nowo.
 */
export const useCompanySelectionStore = create<State & Actions>()(
  devtools(
    trackedCreator(
      (set) => ({
        companies: [],
        selectedCompany: companyFromUrl(),

        setCompanies: (companies) => set({ companies }, false, 'setCompanies'),
        chooseCompany: (company) =>
          set({ selectedCompany: company }, false, 'chooseCompany'),

        syncFromPath: (pathname) =>
          set(
            (state) =>
              matchUrlSegment(pathname, MATCH_URL, UrlSegment.MIDDLE)
                ? state
                : { selectedCompany: companyFromUrl() },
            false,
            'syncFromPath',
          ),
      }),
      'company-selection',
    ),
    { name: 'EasyTacho', store: 'company-selection' },
  ),
)

/** Dla kodu spoza Reacta i dla HOC-ów opakowujących `connect`. */
export const selectCompanyId = (s: State): string =>
  s.selectedCompany?.id ?? ''
